import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";

import useSEO from "../../lib/useSEO";
import {
  adminCreateProduct,
  adminGetProduct,
  adminUpdateProduct,
  adminUpload,
  fetchCategories,
  formatApiError,
  imageUrl,
} from "../../lib/api";
import { FORMAT_IDR } from "../../lib/constants";

const EMPTY = {
  slug: "",
  name: "",
  category: "",
  category_name: "",
  price_from: 0,
  badges: [],
  short_description: "",
  description: "",
  benefits: [""],
  custom_options: [""],
  size_guide: [{ size: "M", chest: "", length: "" }],
  image_ids: [],
  images: [],
  faqs: [{ q: "", a: "" }],
};

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const BADGE_OPTIONS = ["BEST SELLER", "CUSTOM READY"];

export default function AdminProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Keep track of whether user manually edited slug — if not, auto-derive from name
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(isEdit);

  useSEO({
    title: isEdit ? "Edit Produk — Admin" : "Tambah Produk — Admin",
  });

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    adminGetProduct(id)
      .then((p) => {
        const imageIds = (p.images || []).map(img => img.id);
        setForm({
            ...EMPTY,
            ...p,

            image_ids: imageIds,

            benefits: p.benefits?.length
                ? p.benefits
                : [""],

            custom_options: p.custom_options?.length
                ? p.custom_options
                : [""],

            size_guide: p.size_guide?.length
                ? p.size_guide
                : EMPTY.size_guide,

            faqs: p.faqs?.length
                ? p.faqs
                : EMPTY.faqs,
        });
      })
      .catch((err) => {
        toast.error(formatApiError(err?.response?.data?.detail));
        navigate("/admin/products");
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Auto-fill slug + category_name
  const onNameChange = (name) => {
    setForm((s) => ({
      ...s,
      name,
      slug: slugManuallyEdited ? s.slug : slugify(name),
    }));
  };
  const onCategoryChange = (slug) => {
    const c = categories.find((x) => x.slug === slug);
    setForm((s) => ({
      ...s,
      category: slug,
      category_name: c?.name || s.category_name,
    }));
  };

  const toggleBadge = (b) => {
    setForm((s) => ({
      ...s,
      badges: s.badges.includes(b)
        ? s.badges.filter((x) => x !== b)
        : [...s.badges, b],
    }));
  };

  // ---------- Image upload ----------
  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same-file re-pick fires change
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran maksimal 5MB");
      return;
    }
    setUploading(true);
    try {
      const res = await adminUpload(file);

      setForm((s) => ({
          ...s,
          images: [
              ...s.images,
              {
                  id: res.id,
                  url: res.url,
                  filename: res.filename,
              },
          ],
          image_ids: [
              ...s.image_ids,
              res.id,
          ],
      }));
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Gagal upload"
      );
    } finally {
      setUploading(false);
    }
  };
  const removeImage = (index) => {

      setForm((s)=>({

          ...s,

          images: s.images.filter(
              (_,i)=>i!==index
          ),

          image_ids: s.image_ids.filter(
              (_,i)=>i!==index
          )

      }));

  };

  // ---------- Arrays helpers ----------
  const addItem = (key, empty) =>
    setForm((s) => ({ ...s, [key]: [...s[key], empty] }));
  const updateItem = (key, idx, value) =>
    setForm((s) => ({
      ...s,
      [key]: s[key].map((v, i) => (i === idx ? value : v)),
    }));
  const removeItem = (key, idx) =>
    setForm((s) => ({
      ...s,
      [key]: s[key].filter((_, i) => i !== idx),
    }));

  // ---------- Submit ----------
  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // Client-side validation
    if (!form.name.trim() || !form.slug.trim()) {
      toast.error("Nama dan slug wajib diisi");
      return;
    }
    if (!form.category) {
      toast.error("Pilih kategori");
      return;
    }
    if (!form.price_from || form.price_from < 1000) {
      toast.error("Harga minimal Rp1.000");
      return;
    }
    if (form.images.length === 0) {
      toast.error("Minimal 1 foto produk");
      return;
    }

    const payload = {
        slug: form.slug,
        name: form.name,

        category: form.category,
        category_name: form.category_name,

        price_from: Number(form.price_from),

        badges: form.badges,

        short_description: form.short_description,
        description: form.description,

        benefits: form.benefits.filter(x => x.trim()),

        custom_options: form.custom_options.filter(x => x.trim()),

        size_guide: form.size_guide.filter(
            s => s.size.trim() &&
                s.chest.trim() &&
                s.length.trim()
        ),

        image_ids: form.image_ids,

        faqs: form.faqs.filter(
            f => f.q.trim() &&
                f.a.trim()
        ),
    }

    setSaving(true);
    try {
      if (isEdit) {
        await adminUpdateProduct(id, payload);
        toast.success("Produk diupdate");
      } else {
        await adminCreateProduct(payload);
        toast.success("Produk ditambahkan");
      }
      navigate("/admin/products");
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Gagal menyimpan produk"
      );
    } finally {
      setSaving(false);
    }
  };

  const previewPrice = useMemo(() => FORMAT_IDR(form.price_from || 0), [form.price_from]);

  if (loading) {
    return (
      <div className="flex items-center gap-3 py-10 text-[#A3A3A3]">
        <Loader2 className="w-4 h-4 animate-spin text-[#CCFF00]" />
        Memuat produk...
      </div>
    );
  }

  return (
    <div data-testid="admin-product-form-page" className="space-y-6">
      <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[#A3A3A3]">
        <Link
          to="/admin/products"
          data-testid="admin-form-back"
          className="inline-flex items-center gap-1 hover:text-[#CCFF00]"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Kembali
        </Link>
        <span>/</span>
        <span className="text-white">
          {isEdit ? "Edit Produk" : "Tambah Produk"}
        </span>
      </div>

      <form onSubmit={onSubmit} className="space-y-6" data-testid="admin-product-form">
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          {/* Left column */}
          <div className="space-y-6">
            {/* Basic info */}
            <Card title="Info Produk">
              <Field label="Nama Produk *">
                <input
                  data-testid="form-name"
                  required
                  value={form.name}
                  onChange={(e) => onNameChange(e.target.value)}
                  className={inputCls}
                  placeholder="Jersey Futsal Pro Team"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Slug URL *" hint="otomatis dari nama, edit jika perlu">
                  <input
                    data-testid="form-slug"
                    required
                    value={form.slug}
                    onChange={(e) => {
                      setSlugManuallyEdited(true);
                      set("slug", slugify(e.target.value));
                    }}
                    className={inputCls}
                    placeholder="jersey-futsal-pro-team"
                  />
                </Field>
                <Field label="Kategori *">
                  <select
                    data-testid="form-category"
                    required
                    value={form.category}
                    onChange={(e) => onCategoryChange(e.target.value)}
                    className={selectCls}
                  >
                    <option value="">— Pilih kategori —</option>
                    {categories.map((c) => (
                      <option key={c.slug} value={c.slug}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Short Description *" hint="tampil di kartu produk (maks ~200 karakter)">
                <textarea
                  data-testid="form-short-description"
                  required
                  rows={2}
                  value={form.short_description}
                  onChange={(e) => set("short_description", e.target.value)}
                  className={`${inputCls} resize-y`}
                />
              </Field>
              <Field label="Deskripsi Lengkap *" hint="tampil di halaman detail produk">
                <textarea
                  data-testid="form-description"
                  required
                  rows={6}
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  className={`${inputCls} resize-y`}
                />
              </Field>
            </Card>

            {/* Images */}
            <Card title="Foto Produk *" hint="Upload minimal 1 foto (JPG/PNG/WEBP, maks 5MB)">
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={onFile}
                className="hidden"
                data-testid="form-image-input"
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {form.images.map((image, i) => (
                  <div
                    key={image.id}
                    data-testid={`form-image-${i}`}
                    className="relative aspect-[4/5] border border-[#262626] overflow-hidden group"
                  >
                    <img
                      src={imageUrl(image.url)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      data-testid={`form-image-remove-${i}`}
                      className="absolute top-2 right-2 w-8 h-8 bg-[#050505]/90 border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white inline-flex items-center justify-center transition-colors"
                      aria-label="hapus"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {i === 0 && (
                      <span className="absolute bottom-2 left-2 bg-[#CCFF00] text-black text-[10px] font-bold px-2 py-1 tracking-widest">
                        UTAMA
                      </span>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={onPickFile}
                  disabled={uploading}
                  data-testid="form-image-upload"
                  className="aspect-[4/5] border border-dashed border-[#262626] hover:border-[#CCFF00] text-[#A3A3A3] hover:text-[#CCFF00] flex flex-col items-center justify-center gap-2 transition-colors disabled:opacity-60"
                >
                  {uploading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Upload className="w-5 h-5" />
                  )}
                  <span className="text-xs font-bold tracking-widest uppercase">
                    {uploading ? "Uploading..." : "Upload Foto"}
                  </span>
                </button>
              </div>
            </Card>

            {/* Benefits & custom options */}
            <Card title="Benefits">
              <ListEditor
                testid="form-benefits"
                items={form.benefits}
                placeholder="Contoh: Bahan dryfit premium, ringan, dan adem"
                onUpdate={(i, v) => updateItem("benefits", i, v)}
                onAdd={() => addItem("benefits", "")}
                onRemove={(i) => removeItem("benefits", i)}
              />
            </Card>

            <Card title="Yang Bisa Di-Custom">
              <ListEditor
                testid="form-custom-options"
                items={form.custom_options}
                placeholder="Contoh: Warna utama & sekunder"
                onUpdate={(i, v) => updateItem("custom_options", i, v)}
                onAdd={() => addItem("custom_options", "")}
                onRemove={(i) => removeItem("custom_options", i)}
              />
            </Card>

            {/* Size guide */}
            <Card title="Size Guide" hint="Size, lebar dada, panjang (contoh: 50 cm)">
              <div className="space-y-2">
                {form.size_guide.map((row, i) => (
                  <div
                    key={i}
                    data-testid={`form-size-row-${i}`}
                    className="grid grid-cols-[80px_1fr_1fr_40px] gap-2 items-center"
                  >
                    <input
                      className={inputCls}
                      value={row.size}
                      onChange={(e) =>
                        updateItem("size_guide", i, { ...row, size: e.target.value })
                      }
                      placeholder="M"
                    />
                    <input
                      className={inputCls}
                      value={row.chest}
                      onChange={(e) =>
                        updateItem("size_guide", i, { ...row, chest: e.target.value })
                      }
                      placeholder="50 cm"
                    />
                    <input
                      className={inputCls}
                      value={row.length}
                      onChange={(e) =>
                        updateItem("size_guide", i, { ...row, length: e.target.value })
                      }
                      placeholder="69 cm"
                    />
                    <button
                      type="button"
                      onClick={() => removeItem("size_guide", i)}
                      className="w-10 h-10 border border-[#262626] text-white hover:border-[#FF3B30] hover:text-[#FF3B30]"
                      aria-label="hapus"
                    >
                      <Trash2 className="w-4 h-4 mx-auto" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addItem("size_guide", { size: "", chest: "", length: "" })}
                  data-testid="form-size-add"
                  className="btn-ghost !py-2"
                >
                  <Plus className="w-4 h-4" /> Tambah Size
                </button>
              </div>
            </Card>

            {/* FAQs */}
            <Card title="FAQ Produk">
              <div className="space-y-3">
                {form.faqs.map((f, i) => (
                  <div
                    key={i}
                    data-testid={`form-faq-${i}`}
                    className="border border-[#262626] p-3 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                        FAQ #{i + 1}
                      </p>
                      <button
                        type="button"
                        onClick={() => removeItem("faqs", i)}
                        className="text-xs text-[#FF3B30] hover:underline"
                      >
                        Hapus
                      </button>
                    </div>
                    <input
                      className={inputCls}
                      placeholder="Pertanyaan"
                      value={f.q}
                      onChange={(e) =>
                        updateItem("faqs", i, { ...f, q: e.target.value })
                      }
                    />
                    <textarea
                      rows={2}
                      className={`${inputCls} resize-y`}
                      placeholder="Jawaban"
                      value={f.a}
                      onChange={(e) =>
                        updateItem("faqs", i, { ...f, a: e.target.value })
                      }
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addItem("faqs", { q: "", a: "" })}
                  data-testid="form-faq-add"
                  className="btn-ghost !py-2"
                >
                  <Plus className="w-4 h-4" /> Tambah FAQ
                </button>
              </div>
            </Card>
          </div>

          {/* Right column (sticky) */}
          <aside className="space-y-6 lg:sticky lg:top-24 h-max">
            <Card title="Harga *">
              <Field label="Harga Mulai Dari (Rp) *">
                <input
                  data-testid="form-price"
                  type="number"
                  min="1000"
                  step="1000"
                  required
                  value={form.price_from || ""}
                  onChange={(e) => set("price_from", Number(e.target.value))}
                  className={inputCls}
                  placeholder="95000"
                />
              </Field>
              <div className="border border-[#262626] px-4 py-3 bg-[#050505] flex items-baseline justify-between">
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">
                  Preview
                </span>
                <span
                  data-testid="form-price-preview"
                  className="font-display text-xl text-[#CCFF00]"
                >
                  {previewPrice}
                </span>
              </div>
            </Card>

            <Card title="Badges">
              <div className="flex flex-wrap gap-2">
                {BADGE_OPTIONS.map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => toggleBadge(b)}
                    data-testid={`form-badge-${b.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-3 py-2 text-xs font-bold tracking-widest uppercase border transition-colors ${
                      form.badges.includes(b)
                        ? b === "BEST SELLER"
                          ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                          : "bg-[#CCFF00] border-[#CCFF00] text-black"
                        : "border-[#262626] text-white hover:border-[#CCFF00]"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </Card>

            <div className="flex flex-col gap-3">
              <button
                type="submit"
                disabled={saving}
                data-testid="form-submit"
                className="btn-primary disabled:opacity-60 disabled:cursor-wait"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving
                  ? "Menyimpan..."
                  : isEdit
                    ? "Update Produk"
                    : "Simpan Produk"}
              </button>
              <Link
                to="/admin/products"
                data-testid="form-cancel"
                className="btn-ghost text-center"
              >
                Batal
              </Link>
            </div>
          </aside>
        </div>
      </form>
    </div>
  );
}

// ---------- UI helpers ----------
const inputCls =
  "w-full bg-transparent border border-[#262626] focus:border-[#CCFF00] outline-none text-white px-3 py-2.5 transition-colors text-sm";
const selectCls =
  "w-full bg-[#0a0a0a] border border-[#262626] focus:border-[#CCFF00] outline-none text-white px-3 py-2.5 transition-colors text-sm";

function Card({ title, hint, children }) {
  return (
    <div className="border border-[#262626] p-5 space-y-4">
      <div>
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#CCFF00]">
          {title}
        </p>
        {hint && <p className="text-[11px] text-[#A3A3A3] mt-1">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-widest text-[#A3A3A3] block mb-1.5">
        {label}
      </label>
      {children}
      {hint && <p className="text-[11px] text-[#A3A3A3] mt-1">{hint}</p>}
    </div>
  );
}

function ListEditor({ items, placeholder, onUpdate, onAdd, onRemove, testid }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <input
            className={inputCls}
            value={v}
            onChange={(e) => onUpdate(i, e.target.value)}
            placeholder={placeholder}
          />
          <button
            type="button"
            onClick={() => onRemove(i)}
            className="w-10 h-10 border border-[#262626] text-white hover:border-[#FF3B30] hover:text-[#FF3B30] shrink-0"
            aria-label="hapus"
          >
            <Trash2 className="w-4 h-4 mx-auto" />
          </button>
        </div>
      ))}
      <button type="button" onClick={onAdd} className="btn-ghost !py-2">
        <Plus className="w-4 h-4" /> Tambah
      </button>
    </div>
  );
}
